import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container,
  Typography,
  CircularProgress,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import FilterToolbar from './FilterToolbar';
import { debounce } from 'lodash';

const API_URL = '/api/proveedores';

const getDefaultFilters = () => ({
  q: '',
  startDate: '',
  endDate: '',
  statuses: [],
});

const highlightText = (text, query) => {
  if (!query || !text) {
    return text;
  }
  const parts = text.split(new RegExp(`(${query})`, 'gi'));
  return (
    <span>
      {parts.map((part, index) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <mark key={index}>{part}</mark>
        ) : (
          part
        )
      )}
    </span>
  );
};

function ProveedorList() {
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState(getDefaultFilters());
  const [sortConfig, setSortConfig] = useState({ key: 'nombre_fiscal', direction: 'asc' });
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProveedores = debounce(async (currentFilters, currentSortConfig) => {
      try {
        setLoading(true);
        const params = {
          q: currentFilters.q,
          sort_by: currentSortConfig.key,
          sort_direction: currentSortConfig.direction,
        };
        const response = await axios.get(API_URL, { params });
        setProveedores(response.data);
        setError('');
      } catch (error) {
        console.error('Error fetching proveedores:', error);
        setError('No se pudieron cargar los proveedores.');
      } finally {
        setLoading(false);
      }
    }, 500);

    fetchProveedores(filters, sortConfig);

    return () => {
      fetchProveedores.cancel();
    };
  }, [filters, sortConfig]);

  const handleRowClick = (id) => {
    navigate(`/proveedores/${id}`);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Proveedores
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate('/proveedores/nuevo')}
        >
          Crear Nuevo Proveedor
        </Button>
      </Box>

      <FilterToolbar
        filters={filters}
        onFilterChange={setFilters}
        statusOptions={[]}
      />

      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <TableContainer component={Paper} elevation={3}>
          <Table sx={{ minWidth: 650 }} aria-label="proveedores table">
            <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortConfig.key === 'nombre_fiscal'}
                    direction={sortConfig.direction}
                    onClick={() => handleSort('nombre_fiscal')}
                  >
                    Nombre Fiscal
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortConfig.key === 'rfc'}
                    direction={sortConfig.direction}
                    onClick={() => handleSort('rfc')}
                  >
                    RFC
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Teléfono</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {proveedores.map((proveedor) => (
                <TableRow
                  key={proveedor.id}
                  hover
                  onClick={() => handleRowClick(proveedor.id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>{highlightText(proveedor.nombre_fiscal, filters.q)}</TableCell>
                  <TableCell>{highlightText(proveedor.rfc, filters.q)}</TableCell>
                  <TableCell>{highlightText(proveedor.telefono || 'N/A', filters.q)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
}

export default ProveedorList;
